import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Eye, EyeOff, AlertCircle, CheckCircle, Mail } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const RegisterPage = () => {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', email: '', password: '', confirmPassword: '', affiliation: '' });
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [registered, setRegistered] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMsg, setResendMsg] = useState('');

  const handleChange = e => {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
    setError('');
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.name || !form.email || !form.password || !form.affiliation) {
      setError('Please fill in all required fields.');
      return;
    }
    if (form.password.length < 8) {
      setError('Password must be at least 8 characters long.');
      return;
    }
    if (form.password !== form.confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      const result = await register({ name: form.name, email: form.email, password: form.password, affiliation: form.affiliation });
      if (result?.requiresVerification) {
        setRegisteredEmail(form.email);
        setRegistered(true);
      } else {
        navigate('/dashboard/author');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResendLoading(true);
    setResendMsg('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/resend-verification?email=${encodeURIComponent(registeredEmail)}`, {
        method: 'POST',
      });
      const data = await res.json();
      setResendMsg(data.message || 'Verification email sent.');
    } catch {
      setResendMsg('Something went wrong. Please try again.');
    } finally {
      setResendLoading(false);
    }
  };

  const fields = [
    { name: 'name', label: 'Full Name', type: 'text', placeholder: 'Dr. Jane Smith', required: true },
    { name: 'email', label: 'Institutional Email', type: 'email', placeholder: 'jane.smith@university.edu', required: true },
    { name: 'affiliation', label: 'University / Organization', type: 'text', placeholder: 'Massachusetts Institute of Technology', required: true },
  ];

  // ── Success / verification-pending screen ──────────────────────────────────
  if (registered) {
    return (
      <div className="min-h-screen bg-[#F5F7FA] flex items-center justify-center py-12 px-4" data-testid="register-page">
        <div className="w-full max-w-lg">
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-2 mb-6">
              <img src="/mah-logo.png" alt="MAH Quantum Research Institute" className="w-12 h-12 object-cover rounded" />
            </Link>
          </div>
          <div className="bg-white border border-[#E5E7EB] p-8 text-center" data-testid="verification-pending">
            <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-5">
              <Mail size={26} className="text-green-600" />
            </div>
            <h2 className="font-serif text-2xl font-semibold text-[#1F1F1F] mb-3">Check Your Email</h2>
            <p className="text-[#4B5563] text-sm leading-relaxed mb-2">
              We've sent a verification link to:
            </p>
            <p className="font-semibold text-[#1A73E8] text-sm mb-5">{registeredEmail}</p>
            <p className="text-[#6B7280] text-sm leading-relaxed mb-6">
              Click the link in the email to activate your account. The link expires in <strong>24 hours</strong>.
            </p>

            {resendMsg && (
              <div className="flex items-start gap-2 p-3 mb-4 bg-blue-50 border border-blue-200 text-blue-700 text-sm text-left">
                <CheckCircle size={14} className="flex-shrink-0 mt-0.5" />
                {resendMsg}
              </div>
            )}

            <button
              onClick={handleResend}
              disabled={resendLoading}
              className="w-full py-2.5 border border-[#E5E7EB] text-[#374151] text-sm font-medium hover:bg-[#F5F7FA] transition-colors disabled:opacity-60 disabled:cursor-not-allowed mb-4"
            >
              {resendLoading ? 'Sending…' : 'Resend Verification Email'}
            </button>

            <p className="text-xs text-[#9CA3AF]">
              Didn't receive the email? Check your spam folder, or use the button above to resend.
            </p>

            <p className="text-center text-sm text-[#6B7280] mt-5">
              Already verified?{' '}
              <Link to="/login" className="text-[#1A73E8] hover:text-[#174EA6] font-medium">
                Sign In
              </Link>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ── Registration form ──────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-[#F5F7FA] flex items-center justify-center py-12 px-4" data-testid="register-page">
      <div className="w-full max-w-lg">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-6">
            <img src="/mah-logo.png" alt="MAH Quantum Research Institute" className="w-12 h-12 object-cover rounded" />
          </Link>
          <h1 className="font-serif text-3xl font-semibold text-[#1F1F1F] mb-1">Create Author Account</h1>
          <p className="text-[#6B7280] text-sm">Register to submit and track your research papers</p>
        </div>

        <div className="bg-white border border-[#E5E7EB] p-8">
          {error && (
            <div className="flex items-start gap-2.5 p-3 mb-5 bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="register-error">
              <AlertCircle size={15} className="flex-shrink-0 mt-0.5" />
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="register-form">
            {fields.map(field => (
              <div key={field.name}>
                <label htmlFor={field.name} className="block text-sm font-medium text-[#374151] mb-1.5">
                  {field.label} {field.required && <span className="text-red-500">*</span>}
                </label>
                <input
                  id={field.name}
                  name={field.name}
                  type={field.type}
                  value={form[field.name]}
                  onChange={handleChange}
                  placeholder={field.placeholder}
                  className="w-full px-4 py-2.5 border border-[#E5E7EB] text-[#1F1F1F] text-sm placeholder:text-[#9CA3AF] focus:outline-none focus:ring-1 focus:ring-[#1A73E8] focus:border-[#1A73E8] transition-colors"
                  data-testid={`register-${field.name}-input`}
                />
              </div>
            ))}

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-[#374151] mb-1.5">
                Password <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPass ? 'text' : 'password'}
                  value={form.password}
                  onChange={handleChange}
                  placeholder="Minimum 8 characters"
                  className="w-full px-4 py-2.5 pr-10 border border-[#E5E7EB] text-[#1F1F1F] text-sm placeholder:text-[#9CA3AF] focus:outline-none focus:ring-1 focus:ring-[#1A73E8] focus:border-[#1A73E8] transition-colors"
                  data-testid="register-password-input"
                />
                <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]">
                  {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-[#374151] mb-1.5">
                Confirm Password <span className="text-red-500">*</span>
              </label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                value={form.confirmPassword}
                onChange={handleChange}
                placeholder="Re-enter your password"
                className="w-full px-4 py-2.5 border border-[#E5E7EB] text-[#1F1F1F] text-sm placeholder:text-[#9CA3AF] focus:outline-none focus:ring-1 focus:ring-[#1A73E8] focus:border-[#1A73E8] transition-colors"
                data-testid="register-confirm-password-input"
              />
            </div>

            {/* Role note */}
            <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 text-sm text-blue-700">
              <CheckCircle size={14} className="flex-shrink-0 mt-0.5" />
              <span>New accounts are registered as <strong>Author</strong>. Reviewer and Editor roles are assigned by the editorial team.</span>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-[#1A73E8] text-white text-sm font-semibold hover:bg-[#174EA6] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              data-testid="register-submit-button"
            >
              {loading ? 'Creating Account…' : 'Create Account'}
            </button>
          </form>

          <p className="text-center text-sm text-[#6B7280] mt-5">
            Already have an account?{' '}
            <Link to="/login" className="text-[#1A73E8] hover:text-[#174EA6] font-medium" data-testid="register-login-link">
              Sign In
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
